// Runtime-injected config. The container entrypoint writes window._env_ from the
// pod's environment variables (env-config.js, loaded before the app bundle). import.meta.env
// is the local-dev (Vite) fallback. buildSha/environment are build-time — always from
// import.meta.env. All values are PUBLIC client-side config (no secrets).
export type AppConfig = {
  workosClientId: string;
  workosRedirectUri: string;
  workosApiHostname: string | undefined;
  workosPort: number | undefined;
  workosHttps: boolean | undefined;
  entitlementsApiUrl: string;
  ldClientId: string;
  appinsightsConnectionString: string;
  clarityProjectId: string;
  consentPolicyVersion: string;
  ga4MeasurementId: string;
  buildSha: string;
  environment: string;
};

type Env = Record<string, string | undefined>;

declare global {
  interface Window {
    _env_?: Env;
  }
}

// AuthKit https override: 'false' → false, else true; unset → undefined (SDK default: https).
function httpsOverride(raw: string | undefined): boolean | undefined {
  return raw ? raw !== 'false' : undefined;
}

// Exported for unit testing; production uses the `runtimeEnv` singleton below.
export function readConfig(meta: Env): AppConfig {
  const win = (typeof window !== 'undefined' ? window._env_ : undefined) ?? {};
  // `||` (not `??`) so an empty runtime value — public/env-config.js ships empty
  // defaults — falls through to the import.meta.env build/dev value.
  const pick = (runtimeKey: string, buildKey: string) => win[runtimeKey] || meta[buildKey] || '';
  return {
    workosClientId: pick('WORKOS_CLIENT_ID', 'VITE_WORKOS_CLIENT_ID'),
    workosRedirectUri: pick('WORKOS_REDIRECT_URI', 'VITE_WORKOS_REDIRECT_URI'),
    workosApiHostname: (win.WORKOS_API_HOSTNAME || meta.VITE_WORKOS_API_HOSTNAME) || undefined,
    // Emulator endpoint overrides (local dev). Unset in prod → the SDK's real-WorkOS defaults.
    workosPort: Number(win.WORKOS_PORT || meta.VITE_WORKOS_PORT) || undefined,
    workosHttps: httpsOverride(win.WORKOS_HTTPS || meta.VITE_WORKOS_HTTPS),
    // '/api' reaches the Vite dev proxy; deployments inject the API's real origin.
    // Trailing slash stripped once here so every consumer joins paths identically.
    entitlementsApiUrl: (win.ENTITLEMENTS_API_URL || meta.VITE_ENTITLEMENTS_API_URL || '/api').replace(/\/$/, ''),
    ldClientId: pick('LD_CLIENT_ID', 'VITE_LD_CLIENT_ID'),
    appinsightsConnectionString: pick('APPINSIGHTS_CONNECTION_STRING', 'VITE_APPINSIGHTS_CONNECTION_STRING'),
    clarityProjectId: pick('CLARITY_PROJECT_ID', 'VITE_CLARITY_PROJECT_ID'),
    // Consent policy version: a bump re-shows the banner. Defaults to '1' when unset.
    consentPolicyVersion: pick('CONSENT_POLICY_VERSION', 'VITE_CONSENT_POLICY_VERSION') || '1',
    ga4MeasurementId: pick('GA4_MEASUREMENT_ID', 'VITE_GA4_MEASUREMENT_ID'),
    buildSha: meta.VITE_BUILD_SHA ?? 'dev',         // build-time only
    environment: meta.MODE ?? 'production',
  };
}

export const runtimeEnv = readConfig((import.meta as unknown as { env: Env }).env);
